// Authentication Context for LinkedIn Profile Optimizer
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(false);
  
  // Deduplication: track in-flight fetch requests to prevent duplicates
  const fetchInProgress = useRef(null);

  // Fetch user data from MongoDB (with deduplication)
  const fetchUserData = async (firebaseUser) => {
    if (!firebaseUser) {
      setUserCredits(0);
      return null;
    }

    const userEmail = firebaseUser.email?.toLowerCase();
    
    // If a fetch is already in progress for this email, wait for it
    if (fetchInProgress.current && fetchInProgress.current.email === userEmail) {
      console.log('⏳ [AUTH] Fetch already in progress for:', userEmail);
      return fetchInProgress.current.promise;
    }

    // Create a new fetch promise
    const fetchPromise = (async () => {
      try {
        setUserDataLoading(true);
        const apiUrl = process.env.NODE_ENV === 'production' 
          ? '/api/user/get-or-create'
          : 'http://localhost:3001/api/user/get-or-create';

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: firebaseUser.email,
            name: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            uid: firebaseUser.uid
          })
        });

        const data = await response.json();
        if (data.success) {
          setUserCredits(data.user.creditsAvailable || 0);
          return data.user;
        }
        return null;
      } catch (error) {
        console.error('Error fetching user data:', error);
        return null;
      } finally {
        setUserDataLoading(false);
        fetchInProgress.current = null;
      }
    })();

    // Store the in-flight request
    fetchInProgress.current = { email: userEmail, promise: fetchPromise };
    
    return fetchPromise;
  };

  // Update user credits after use
  const useCredit = async () => {
    if (!user) return { success: false, error: 'Not logged in' };

    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/user/use-credit'
        : 'http://localhost:3001/api/user/use-credit';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });

      const data = await response.json();
      if (data.success) {
        setUserCredits(data.creditsRemaining);
        return { success: true, creditsRemaining: data.creditsRemaining };
      }
      return { success: false, error: data.error };
    } catch (error) {
      console.error('Error using credit:', error);
      return { success: false, error: error.message };
    }
  };

  // Refresh user credits from server
  const refreshCredits = async () => {
    if (!user) return;
    
    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/user/credits?email=${encodeURIComponent(user.email)}`
        : `http://localhost:3001/api/user/credits?email=${encodeURIComponent(user.email)}`;

      const response = await fetch(apiUrl);
      const data = await response.json();
      
      if (data.success) {
        setUserCredits(data.creditsAvailable);
      }
    } catch (error) {
      console.error('Error refreshing credits:', error);
    }
  };

  // Manual email/password login
  const manualLogin = async (email, password) => {
    try {
      setUserDataLoading(true);
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/user/manual-login'
        : 'http://localhost:3001/api/user/manual-login';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      
      if (data.success && data.user) {
        // Set user state with manual login data
        setUser({
          uid: data.user._id?.toString() || 'manual-' + Date.now(),
          email: data.user.email,
          displayName: data.user.name || data.user.email.split('@')[0],
          photoURL: data.user.photoURL || '',
          isManualAuth: true
        });
        setUserCredits(data.user.creditsAvailable || 0);
        return { success: true, user: data.user };
      }
      
      return { success: false, error: data.error || 'Login failed' };
    } catch (error) {
      console.error('Manual login error:', error);
      return { success: false, error: error.message };
    } finally {
      setUserDataLoading(false);
    }
  };

  // Manual logout (for manual auth users)
  const manualLogout = () => {
    setUser(null);
    setUserCredits(0);
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL
        });
        await fetchUserData(firebaseUser);
      } else {
        setUser(null);
        setUserCredits(0);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = {
    user,
    userCredits,
    loading,
    userDataLoading,
    useCredit,
    refreshCredits,
    fetchUserData,
    manualLogin,
    manualLogout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
